import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { apiRequest } from "@/lib/queryClient";

// Quante volte proporre il soft-ask prima di smettere del tutto.
const MAX_ASKS = 2;
// Distanza minima tra due proposte: se dice "ora no", non lo ributtiamo li' subito.
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 giorni

const LS_COUNT = "push_optin_count";
const LS_LAST = "push_optin_last";
const LS_NEVER = "push_optin_never";

// Stesse pagine escluse della modale marketing, piu' la chat: li' l'utente sta
// gia' leggendo il messaggio, quindi la notifica gli serve meno in quel momento.
const HIDDEN_PATHS = [
  "/auth",
  "/register",
  "/register-wizard",
  "/forgot-password",
  "/reset-password",
  "/activate",
  "/onboarding",
  "/admin",
  "/admin-login",
  "/checkout",
  "/privacy",
  "/termini",
  "/termini-e-policy",
  "/cookie-policy",
  "/reclami",
  "/dmca",
  "/2257",
  "/contenuti-vietati",
  "/rimozione-contenuti",
  "/amanti-ai",
];

/**
 * Soft-ask per le notifiche push.
 *
 * NON chiama mai Notification.requestPermission() da solo: il prompt del browser
 * parte solo dopo un "Sì" esplicito qui dentro. Il motivo e' che se l'utente
 * clicca "Blocca" nel prompt nativo la decisione e' definitiva (il sito non puo'
 * piu' richiederlo), mentre un "no" a questa card non costa nulla e si puo'
 * riproporre piu' avanti.
 *
 * Momento scelto: subito dopo aver ricevuto un messaggio, quando il beneficio
 * e' evidente. Si aggancia all'evento `cercamanti:message-received`.
 */
export function PushOptInPrompt() {
  const { isAuthenticated } = useAuth();
  const [location] = useLocation();
  const { isPushSupported, permissionState, isSubscribed, subscribeToPush } =
    usePushNotifications();

  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);

  // La modale marketing ha la precedenza: due richieste di consenso di fila
  // sono il modo piu' rapido per farsi dire di no a entrambe.
  const { data: marketing } = useQuery<{ decided: boolean; shouldPrompt: boolean }>({
    queryKey: ["/api/marketing-consent"],
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  const isHiddenPath = HIDDEN_PATHS.some(
    (p) => location === p || location.startsWith(`${p}/`),
  );

  // Se il permesso del browser risulta deciso (granted o denied), il soft-ask
  // non ha piu' senso: lo marchiamo come chiuso per sempre. Un "denied" e'
  // definitivo — il sito non puo' piu' richiederlo — quindi insistere sarebbe
  // solo fastidio senza alcuna possibilita' di riuscita.
  useEffect(() => {
    if (permissionState === "denied" || permissionState === "granted") {
      localStorage.setItem(LS_NEVER, "1");
      setOpen(false);
    }
  }, [permissionState]);

  useEffect(() => {
    // Chiediamo solo se: push supportate, permesso ancora "default" (mai deciso
    // a livello browser), non gia' iscritto, e nessuna richiesta marketing pendente.
    const canAsk =
      isAuthenticated &&
      isPushSupported &&
      permissionState === "default" &&
      !isSubscribed &&
      !isHiddenPath &&
      marketing?.shouldPrompt === false;

    if (!canAsk) return;

    if (localStorage.getItem(LS_NEVER) === "1") return;
    if (Number(localStorage.getItem(LS_COUNT) || 0) >= MAX_ASKS) return;

    const last = Number(localStorage.getItem(LS_LAST) || 0);
    if (last && Date.now() - last < COOLDOWN_MS) return;

    const onMessage = () => {
      // I gate vanno riletti ANCHE qui: quelli sopra sono stati valutati una
      // volta sola quando il listener e' stato agganciato, quindi senza questo
      // ricontrollo un secondo messaggio riaprirebbe la card anche dopo che
      // cap/cooldown/never sono scattati.
      if (localStorage.getItem(LS_NEVER) === "1") return;
      if (Number(localStorage.getItem(LS_COUNT) || 0) >= MAX_ASKS) return;
      const lastShown = Number(localStorage.getItem(LS_LAST) || 0);
      if (lastShown && Date.now() - lastShown < COOLDOWN_MS) return;

      // Il conteggio si incrementa all'APERTURA, non alla chiusura: se contassimo
      // solo alla chiusura, chi ignora la card se la vedrebbe riaprire a ogni
      // messaggio successivo.
      setOpen((alreadyOpen) => {
        if (alreadyOpen) return true;
        localStorage.setItem(
          LS_COUNT,
          String(Number(localStorage.getItem(LS_COUNT) || 0) + 1),
        );
        localStorage.setItem(LS_LAST, String(Date.now()));
        return true;
      });
    };
    window.addEventListener("cercamanti:message-received", onMessage);
    return () => window.removeEventListener("cercamanti:message-received", onMessage);
  }, [
    isAuthenticated,
    isPushSupported,
    permissionState,
    isSubscribed,
    isHiddenPath,
    marketing?.shouldPrompt,
  ]);

  const close = (never: boolean) => {
    setOpen(false);
    // Il contatore e' gia' stato incrementato all'apertura: qui aggiorniamo solo
    // il timestamp (per il cooldown) ed eventualmente il flag definitivo.
    localStorage.setItem(LS_LAST, String(Date.now()));
    if (never) localStorage.setItem(LS_NEVER, "1");
  };

  const handleAccept = async () => {
    setPending(true);
    try {
      // Solo QUI parte il prompt nativo del browser, dopo un sì esplicito.
      const ok = await subscribeToPush();
      if (ok) {
        // Allinea le preferenze: chi accetta qui vuole gli avvisi sui messaggi.
        await apiRequest("PUT", "/api/notification-preferences", { messages: true });
      }
    } catch {
      // Errore di rete o iscrizione fallita: non insistiamo comunque.
    } finally {
      setPending(false);
      // Chiudiamo sempre in modo definitivo, a prescindere dall'esito: l'utente
      // ha gia' fatto la sua scelta e ripresentare la card sarebbe insistere.
      // Se qualcosa e' andato storto puo' attivarle da Impostazioni account.
      close(true);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Attiva le notifiche"
      className="fixed inset-0 z-[10040] flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        onClick={() => close(false)}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      />

      <div className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-primary/40 bg-card text-card-foreground shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="h-1 bg-gradient-to-r from-primary via-primary/70 to-primary/30" />

        <div className="relative p-6 text-center">
          <button
            type="button"
            onClick={() => close(false)}
            disabled={pending}
            aria-label="Chiudi"
            className="absolute right-3 top-3 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            data-testid="button-push-optin-dismiss"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border-2 border-primary/50 bg-primary/5">
            <Bell className="h-6 w-6 text-primary" />
          </div>

          <h2 className="mt-4 text-lg font-bold leading-tight">
            Non farti trovare offline
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Ti avvisiamo quando ricevi un messaggio, così puoi rispondere subito
            anche quando non hai l'app aperta.
          </p>

          <div className="mt-5 space-y-1">
            <Button
              className="h-11 w-full rounded-full text-sm font-bold uppercase tracking-wide shadow-md"
              onClick={handleAccept}
              disabled={pending}
              data-testid="button-push-optin-accept"
            >
              Attiva le notifiche
            </Button>
            <button
              type="button"
              onClick={() => close(false)}
              disabled={pending}
              className="w-full rounded-md py-2 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
              data-testid="button-push-optin-decline"
            >
              Ora no
            </button>
          </div>

          <p className="mt-3 text-[11px] leading-snug text-muted-foreground">
            Puoi disattivarle quando vuoi dalle impostazioni del tuo account.
          </p>
        </div>
      </div>
    </div>
  );
}
